<script setup>
import { useHead } from "#imports";
import { useIdle } from "@vueuse/core";
import { computed, h, nextTick, onMounted, ref, watch } from "vue";
import VoixVideoControls from "./VoixVideoControls.vue";
const props = defineProps({
  src: { type: String, required: true },
  options: { type: Object, required: true },
  width: { type: null, required: true },
  height: { type: null, required: true }
});
let player = null;
const paused = ref(true);
const muted = ref(false);
const volume = ref(0);
const showControls = ref(false);
const { idle, reset } = useIdle(1e3);
const cloudflareId = computed(() => {
  const regex = /iframe\.cloudflarestream\.com\/([^/]+)/;
  const match = props.src.match(regex);
  return match ? match[1] : void 0;
});
const cloudflareControls = computed(() => {
  return props.options.controls.mode === "cloudflare" && props.options.controls.display;
});
const cloudflareAutoplay = computed(() => {
  return props.options.autoplay ? "autoplay=true" : "";
});
const cloudflareMuted = computed(() => {
  return props.options.mute ? "muted=true" : "";
});
const videoStyles = computed(() => {
  const styles = {
    aspectRatio: props.options.style.aspectRatio ? `aspect-ratio: ${props.options.style.aspectRatio}; ` : "",
    width: props.options.style.respectWidth ? `width: ${props.width}px; ` : "",
    height: props.options.style.respectHeight ? `height: ${props.height}px; ` : ""
  };
  return styles.aspectRatio + styles.width + styles.height;
});
function play() {
  paused.value = false;
  player.play();
}
function pause() {
  paused.value = true;
  player.pause();
}
function mute() {
  muted.value = true;
  player.muted = true;
}
function unMute() {
  muted.value = false;
  player.muted = false;
}
function setVolume({ value, init = false }) {
  volume.value = value;
  player.volume = value / 100;
  if (!init && Number(value) === 0)
    mute();
  else
    unMute();
}
function fullscreen() {
  const iframe = document.getElementById("stream-player");
  if (iframe.requestFullscreen) {
    iframe.requestFullscreen();
  } else if (iframe.mozRequestFullScreen) {
    iframe.mozRequestFullScreen();
  } else if (iframe.webkitRequestFullscreen) {
    iframe.webkitRequestFullscreen();
  } else if (iframe.msRequestFullscreen) {
    iframe.msRequestFullscreen();
  }
}
watch(idle, (value) => {
  if (value) {
    showControls.value = false;
  } else {
    showControls.value = true;
  }
});
onMounted(async () => {
  const CloudflarePlayer = new Promise((resolve) => {
    const checkCloudflare = () => {
      if (typeof Stream === "function" && Stream(document.getElementById("stream-player")) !== null) {
        resolve();
      } else {
        setTimeout(checkCloudflare, 1e3);
      }
    };
    checkCloudflare();
  });
  await CloudflarePlayer.then(() => {
    const iframe = document.getElementById("stream-player");
    player = Stream(iframe);
    setVolume({ value: props.options.volume, init: true });
    if (props.options.mute)
      mute();
    if (props.options.autoplay) {
      play();
    }
  });
});
useHead({
  script: [
    {
      src: "https://embed.cloudflarestream.com/embed/sdk.latest.js",
      async: true
    }
  ]
});
</script>

<template>
  <div class="relative">
    <div :style="videoStyles" v-bind="$attrs">
      <iframe
        id="stream-player"
        :src="`https://iframe.cloudflarestream.com/${cloudflareId}?${cloudflareAutoplay}&${cloudflareMuted}&controls=${cloudflareControls}&loop=${options.loop}`"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowfullscreen="true"
        style="width: 100%; height: 100%; border: none;"
      />
    </div>

    <slot v-if="options.controls.mode === 'voix' && options.controls.display" :play="play" :pause="pause" :paused="paused" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen">
      <div
        class="flex absolute top-0 left-0 h-full w-full text-white duration-200"
        :class="{ 'opacity-0': !showControls, 'opacity-100': showControls || paused }"
        @mouseenter="showControls = true;
  reset()"
        @mousemove="showControls = true;
  reset()"
        @mouseleave="showControls = false"
      >
        <VoixVideoControls :paused="paused" :play="play" :pause="pause" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen" :controls-position="options.controls.position" />
      </div>
    </slot>
  </div>
</template>

<style scoped>
*{border-radius:0!important}
</style>
